import { useCallback, useRef } from 'react';
import type React from 'react';
import type { Vec2 } from '@/lib/companion/types';

const DRAG_THRESHOLD_PX = 4;

export interface DragCallbacks {
  onDragStart(): void;
  onDragMove(pos: Vec2): void;
  onDragEnd(pos: Vec2): void;
  onClick(): void;
  onCancel(): void;
}

export interface DragHandlers {
  onPointerDown: React.PointerEventHandler;
  onPointerMove: React.PointerEventHandler;
  onPointerUp: React.PointerEventHandler;
  onPointerCancel: React.PointerEventHandler;
  onLostPointerCapture: React.PointerEventHandler;
  onDragStart: React.DragEventHandler;
}

interface Gesture {
  pointerId: number;
  startX: number;
  startY: number;
  grabOffsetX: number;
  grabOffsetY: number;
  dragging: boolean;
}

export function useCompanionDrag(callbacks: DragCallbacks): DragHandlers {
  const gestureRef = useRef<Gesture | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const positionFor = (event: React.PointerEvent, gesture: Gesture): Vec2 => ({
    x: event.clientX - gesture.grabOffsetX,
    y: event.clientY - gesture.grabOffsetY,
  });

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    // A gesture is already in flight. Letting a second pointer replace it
    // strands the first: its pointerup no longer matches the stored id, so
    // the drag never ends and interaction ownership is never handed back.
    if (gestureRef.current) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      grabOffsetX: event.clientX - rect.left,
      grabOffsetY: event.clientY - rect.top,
      dragging: false,
    };

    target.setPointerCapture?.(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    if (!gesture.dragging) {
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

      gesture.dragging = true;
      callbacksRef.current.onDragStart();
    }

    callbacksRef.current.onDragMove(positionFor(event, gesture));
  }, []);

  const finish = useCallback((event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    gestureRef.current = null;
    (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);

    if (gesture.dragging) callbacksRef.current.onDragEnd(positionFor(event, gesture));
    else callbacksRef.current.onClick();
  }, []);

  const cancel = useCallback((event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    gestureRef.current = null;
    (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
    callbacksRef.current.onCancel();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: finish,
    onPointerCancel: cancel,
    // The browser can revoke capture without ever sending pointerup — a
    // re-render of the captured element does it. Without this the gesture is
    // never finished, so the companion stays held: frozen, floating, and
    // deaf to clicks because the stale gesture swallows them.
    onLostPointerCapture: cancel,
    // Suppress the browser's native drag ghost; the canvas is an image source.
    onDragStart: useCallback((event: React.DragEvent) => { event.preventDefault(); }, []),
  };
}
